import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  effect,
  inject,
  input,
  isDevMode,
  model,
  output,
} from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Directionality } from '@angular/cdk/bidi';
import { Overlay } from '@angular/cdk/overlay';
import { nextDialogIds } from './dialog-id';
import { DialogSurfaceComponent } from './dialog-surface';
import { DialogVariant } from './dialog-types';

/** How a dialog was closed — surfaced by the `closed` output. */
export type DialogCloseReason = 'programmatic' | 'backdrop' | 'escape' | 'close-button';

/**
 * A themeable modal dialog built on `@angular/cdk/dialog` (centered, scrimmed, focus-trapped).
 *
 * Figma: Core Components — "Dialog" organism (file VFBn7KCDy3FSIlvhNo3ylq, 873:9194).
 *
 * One component, switched by `variant`:
 * - `modal-leading-icon`  — leading icon + title header, projected body, split footer.
 * - `modal-centered-icon` — centered icon badge, centered title + description, split footer.
 * - `alert`               — compact title + description, right-aligned footer.
 *
 * Open state is a two-way `[(open)]` model wrapped around the CDK `Dialog` service. The
 * CDK dialog container provides `role="dialog"`, `aria-modal`, the focus trap, initial
 * focus (`autoFocus: 'dialog'`) and focus restoration. The dialog closes on Escape /
 * backdrop click (when `dismissible`); those are handled here so `closed` can report a
 * reason. Title/description come from inputs; project the body as default content and each
 * action button with the `cmpDialogActions` attribute (the footer lays them out — split
 * full-width for modals, right-aligned for alerts).
 *
 *   <compsych-dialog [(open)]="showDialog" variant="modal-leading-icon"
 *     icon="globe" title="Update your details">
 *     <p>Body content…</p>
 *     <compsych-button cmpDialogActions variant="outlined" fullWidth label="Cancel" (click)="showDialog = false" />
 *     <compsych-button cmpDialogActions variant="filled" fullWidth label="Save" (click)="save()" />
 *   </compsych-dialog>
 *
 * Consumers must load `@angular/cdk/overlay-prebuilt.css` (overlay positioning) and the
 * `@compsych-ui-components/shared` design tokens globally. The scrim color is injected by
 * the dialog itself — no extra stylesheet import is needed.
 */
@Component({
  selector: 'compsych-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DialogSurfaceComponent],
  templateUrl: './dialog.html',
  styleUrl: './dialog.css',
  host: { class: 'cmp-dialog-host' },
})
export class DialogComponent implements OnDestroy {
  /** Two-way open state. Set to `true` to show the dialog, `false` to close it. */
  readonly open = model<boolean>(false);
  /** Layout type. Defaults to `modal-leading-icon`. */
  readonly variant = input<DialogVariant>('modal-leading-icon');
  /**
   * Optional width override — any CSS length (e.g. `'1000px'`). Defaults to the variant
   * width (800px modal / 600px alert). Always clamped to the viewport on small screens.
   */
  readonly width = input<string>('');
  /** Heading text. Also drives the accessible name via `aria-labelledby`. */
  readonly title = input<string>('');
  /** Supporting text under the title (rendered for `centered` and `alert` variants). */
  readonly description = input<string>('');
  /** Lucide icon name — the leading header icon (`leading`) or centered badge glyph (`centered`). */
  readonly icon = input<string>('');
  /**
   * When true (default) the dialog shows a close button and closes on Escape / backdrop click.
   * When false, the dialog is a forced choice — provide a focusable action (e.g. a
   * `[cmpDialogActions]` button) so keyboard users can dismiss it (WCAG 2.1.2, no keyboard trap).
   */
  readonly dismissible = input<boolean>(true);
  /** Accessible label for the close button. */
  readonly closeLabel = input<string>('Close dialog');
  /** Accessible name used when no `title` is provided (e.g. a fully custom body). */
  readonly ariaLabel = input<string>('');
  /** Renders the action footer. Set false to hide it when there are no actions. */
  readonly showActions = input<boolean>(true);

  /** Emits after the dialog has been opened and shown. */
  readonly opened = output<void>();
  /** Emits after the dialog has closed, with the reason it closed. */
  readonly closed = output<DialogCloseReason>();

  /** The dialog surface template, projected into the CDK dialog container when open. */
  @ViewChild('panel', { static: true }) private panelTemplate!: TemplateRef<unknown>;

  private readonly dialog = inject(Dialog);
  private readonly overlay = inject(Overlay);
  private readonly directionality = inject(Directionality);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private dialogRef?: DialogRef<unknown, unknown>;
  private closeReason: DialogCloseReason = 'programmatic';

  private readonly _ids = nextDialogIds();
  protected readonly titleId = this._ids.titleId;
  protected readonly descId = this._ids.descId;

  constructor() {
    // Reflect the open model onto the CDK dialog lifecycle.
    effect(() => {
      if (this.open() && !this.dialogRef) {
        this.attach();
      } else if (!this.open() && this.dialogRef) {
        this.detach();
      }
    });
  }

  ngOnDestroy(): void {
    this.dialogRef?.close();
  }

  /** Close request from the close button. */
  protected requestClose(): void {
    this.closeWith('close-button');
  }

  private closeWith(reason: DialogCloseReason): void {
    if (!this.open()) {
      return;
    }
    this.closeReason = reason;
    this.open.set(false);
  }

  private attach(): void {
    this.closeReason = 'programmatic';

    // Dev-only guardrail: a dialog with no title and no ariaLabel has no accessible
    // name (WCAG 2.2 §4.1.2). Stripped from production builds by isDevMode().
    if (isDevMode() && !this.title() && !this.ariaLabel()) {
      console.warn(
        '[compsych-dialog] Opened with no accessible name — set `title` or `ariaLabel` ' +
          'so assistive technology can identify the dialog (WCAG 2.2 §4.1.2).',
      );
    }

    const ref = this.dialog.open(this.panelTemplate, {
      viewContainerRef: this.viewContainerRef,
      hasBackdrop: true,
      // Scrim color is injected by the dialog surface itself; positioning/fade come from
      // @angular/cdk/overlay-prebuilt.css (which consumers load).
      backdropClass: 'cmp-dialog-scrim',
      panelClass: 'cmp-dialog-pane',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      // Forward the local reading direction so a body-portaled overlay mirrors correctly
      // even when only a subtree (not the document) is RTL.
      direction: this.directionality.value,
      // The `alert` variant is a decision/confirmation dialog → assertive alertdialog role.
      role: this.variant() === 'alert' ? 'alertdialog' : 'dialog',
      // We handle Escape / backdrop ourselves so `closed` can report a reason.
      disableClose: true,
      // Focus the dialog container on open (screen reader announces it; first Tab then
      // lands on the close button with its visible ring) and restore focus on close.
      autoFocus: 'dialog',
      restoreFocus: true,
      ariaModal: true,
      ariaLabelledBy: this.title() ? this.titleId : null,
      ariaDescribedBy:
        this.variant() !== 'modal-leading-icon' && this.description() ? this.descId : null,
      // Accessible name for the no-title custom-body case (CDK nulls aria-labelledby when set).
      ariaLabel: !this.title() ? this.ariaLabel() || null : null,
    });
    this.dialogRef = ref;

    ref.backdropClick.subscribe(() => {
      if (this.dismissible()) {
        this.closeWith('backdrop');
      }
    });
    ref.keydownEvents.subscribe((event) => {
      if (event.key === 'Escape' && this.dismissible()) {
        event.preventDefault();
        this.closeWith('escape');
      }
    });
    ref.closed.subscribe(() => {
      this.dialogRef = undefined;
      const reason = this.closeReason;
      this.closeReason = 'programmatic';
      // Keep the model in sync if the dialog was torn down by CDK rather than the model.
      if (this.open()) {
        this.open.set(false);
      }
      this.closed.emit(reason);
    });

    this.opened.emit();
  }

  private detach(): void {
    // Triggers `ref.closed`, which clears state, restores focus, and emits `closed`.
    this.dialogRef?.close();
  }
}
